import { useState, useEffect } from "react";
import {
  Phone,
  Users,
  Save,
  Check,
  Brain,
  AlertTriangle,
  Bell,
  BellOff,
  BookUser,
} from "lucide-react";
import { useContactsApi } from "../../api/contactsApi";
import Accordion from "../Accordion";
import AnalysisCoverage from "../analysis/AnalysisCoverage";

const ClientSidebar = ({
  activeUser,
  api,
  toast,
  selectedChat,
  setConversations,
  setSelectedChat,
  summarizeConversation,
  deleteConversation,
  users = [],
  tags = [],
  funnels = [],
}) => {
  const [openSection, setOpenSection] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [notes, setNotes] = useState({});
  const [conversationNote, setConversationNote] = useState("");
  const [searchTag, setSearchTag] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [checkingGoogle, setCheckingGoogle] = useState(false);
  const [googleState, setGoogleState] = useState(null);
  const contactsApi = useContactsApi();

  useEffect(() => {
    if (!selectedChat) return;

    // Estado inicial vem do contato carregado; o botão o substitui pela
    // consulta ao vivo quando alguém clica.
    setGoogleState(
      selectedChat.contact?.googleSyncStatus
        ? {
            status: selectedChat.contact.googleSyncStatus,
            error: selectedChat.contact.googleSyncError,
          }
        : null,
    );
    setSelectedStatus(selectedChat.status);
    setSelectedTags(selectedChat.tags?.map((t) => t.id) ?? []);
    setSelectedFunnel(selectedChat.funnel);
    setSelectedUsers(selectedChat.users?.map((u) => u.id) ?? []);
    setNotes(selectedChat.notes);
  }, [selectedChat]);

  const STATUS_OPTIONS = [
    { value: "ABERTO", color: "green" },
    { value: "ATENDIMENTO", color: "yellow" },
    { value: "AGUARDANDO", color: "blue" },
    { value: "RESOLVIDO", color: "indigo" },
    { value: "FECHADO", color: "red" },
    { value: "ARQUIVADO", color: "amber" },
    { value: "INDEFINIDO", color: "gray" },
  ];

  if (!selectedChat) return null;

  const toggleSection = (section) =>
    setOpenSection(openSection === section ? null : section);

  const filteredTags = tags.filter((t) =>
    t.name.toLowerCase().includes(searchTag.toLowerCase()),
  );
  const analysis = selectedChat.analyses?.[0];
  const autoAnalysisEnabled = selectedChat.autoAnalysisEnabled !== false;

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      const result = await summarizeConversation(selectedChat.id);
      setSelectedChat((current) => current?.id === selectedChat.id
        ? { ...current, summary: result.summary, analyses: [result], analysisPending: false }
        : current);
      toast.success("Conversa analisada com sucesso");
    } catch (error) {
      toast.error(error.message || "Não foi possível analisar a conversa");
    } finally {
      setAnalyzing(false);
    }
  };

  // Consulta a agenda na hora, mesmo se o contato já estiver marcado como
  // salvo: quem aperta o botão quer conferir agora, não ler o estado gravado.
  const handleCheckGoogle = async () => {
    const contactId = selectedChat.contact?.id;
    if (!contactId) return;

    try {
      setCheckingGoogle(true);
      const result = await contactsApi.googleSync(contactId);
      setGoogleState(result);

      if (result.status === "SYNCED") {
        toast.success(
          result.alreadyExisted
            ? "Já está cadastrado na agenda do Google."
            : "Não estava cadastrado — salvamos agora na agenda.",
        );
      } else if (result.status === "SKIPPED") {
        toast("Contato sem telefone para consultar.");
      } else {
        toast.error(result.error || "Não foi possível consultar a agenda.");
      }
    } catch (error) {
      toast.error(error.message || "Não foi possível consultar a agenda.");
    } finally {
      setCheckingGoogle(false);
    }
  };

  // Pausa o reprocessamento automático deste cliente sem tirar o botão de
  // análise manual acima.
  const handleToggleAutoAnalysis = async () => {
    try {
      setTogglingAuto(true);
      const enabled = !autoAnalysisEnabled;
      const updated = await api.setAutoAnalysis(selectedChat.id, enabled);

      const apply = (conversation) =>
        conversation?.id === selectedChat.id
          ? {
              ...conversation,
              autoAnalysisEnabled: updated.autoAnalysisEnabled,
              analysisPending: updated.analysisPending,
            }
          : conversation;

      setConversations((prev) => prev.map(apply));
      setSelectedChat((current) => apply(current));

      toast.success(
        enabled
          ? "Análise automática retomada para este cliente"
          : "Análise automática pausada para este cliente",
      );
    } catch (error) {
      toast.error(error.message || "Não foi possível alterar a análise automática");
    } finally {
      setTogglingAuto(false);
    }
  };

  const handleChange = async (payload) => {
    try {
      const id = selectedChat.id;

      const updatedData = await api.update(id, payload);

      setConversations((prev) =>
        prev.map((c) => (c.id === id ? updatedData : c)),
      );

      toast.success("Atualização Bem Sucedida");

      setSelectedChat((prev) => {
        if (!prev) return prev;
        if (prev.id !== id) return prev;

        return updatedData;
      });
    } catch (error) {
      toast.error(error);
      console.error("Erro ao atualizar status:", error);
    }
  };

  const statusColorClasses = {
    green:
      "border-green-400 bg-green-50 text-green-700 dark:border-green-500 dark:bg-green-900/20 dark:text-green-300",
    yellow:
      "border-yellow-400 bg-yellow-50 text-yellow-700 dark:border-yellow-500 dark:bg-yellow-900/20 dark:text-yellow-300",
    blue: "border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/20 dark:text-blue-300",
    gray: "border-gray-400 bg-gray-50 text-gray-700 dark:border-gray-500 dark:bg-gray-900/20 dark:text-gray-300",
    red: "border-red-400 bg-red-50 text-red-700 dark:border-red-500 dark:bg-red-900/20 dark:text-red-300",
    amber:
      "border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-900/20 dark:text-amber-300",
    indigo:
      "border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-300",
  };

  return (
    <div className="w-72 overflow-y-auto border-l border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4 dark:border-gray-700 dark:from-gray-900 dark:to-gray-800">
      {/* FOTO + DADOS DO CLIENTE */}
      <div className="mb-8 flex flex-col items-center">
        <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-green-600 text-3xl font-bold text-white shadow-lg ring-4 ring-green-100 dark:ring-green-900/30">
          <img
            src={selectedChat.contact.profilePic || "/default-avatar.png"}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/default-avatar.png";
            }}
            alt="Avatar"
            className="h-full w-full object-cover"
          />
        </div>
        <h3 className="mt-4 text-center text-xl font-bold text-gray-900 dark:text-gray-100">
          {selectedChat.contact.name || "Cliente sem nome"}
        </h3>
        <div className="mt-2 flex items-center space-x-2 rounded-full bg-gray-100 px-4 py-1.5 text-sm text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          <Phone className="h-3.5 w-3.5 text-green-500" />
          <span className="font-medium">
            {selectedChat.contact.phoneNumber.replace(/\D/g, "")}
          </span>
        </div>
      </div>

      {/* STATUS */}
      <Accordion
        id="status"
        title="Status"
        open={openSection === "status"}
        onToggle={toggleSection}
      >
        <div className="flex flex-col space-y-2">
          {STATUS_OPTIONS.map((status) => (
            <label
              key={status.value}
              onClick={() => setSelectedStatus(status.value)} // 👈 aqui
              className={`flex cursor-pointer items-center justify-between rounded-lg border-2 p-3 text-sm font-medium transition-all duration-200 ${
                selectedStatus === status.value
                  ? statusColorClasses[status.color]
                  : "dark:hover:bg-gray-750 border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600"
              }`}
            >
              <span>{status.value}</span>
              <div
                className={`h-5 w-5 rounded-full border-2 ${
                  selectedStatus === status.value
                    ? "border-current bg-current"
                    : "border-gray-300 dark:border-gray-600"
                } flex items-center justify-center`}
              >
                {selectedStatus === status.value && (
                  <Check className="h-3 w-3 text-white" />
                )}
              </div>
            </label>
          ))}

          <button
            type="button"
            onClick={() => handleChange({ status: selectedStatus })}
            className="mt-2 flex items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95"
          >
            <Save className="h-4 w-4" />
            <span>Confirmar</span>
          </button>
        </div>
      </Accordion>

      {/* TAGS */}
      <Accordion
        id="tags"
        title="Tags"
        open={openSection === "tags"}
        onToggle={toggleSection}
      >
        <input
          type="text"
          placeholder="Buscar tags..."
          value={searchTag}
          onChange={(e) => setSearchTag(e.target.value)}
          className="w-full rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-green-400 focus:ring-2 focus:ring-green-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:border-green-500 dark:focus:ring-green-900/30"
        />
        <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
          {filteredTags.map((tag) => (
            <label
              key={tag.id}
              className="dark:hover:bg-gray-750 flex cursor-pointer items-center justify-between rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-sm transition-all duration-200 hover:border-green-300 hover:bg-green-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-green-600"
            >
              <span className="font-medium text-gray-800 dark:text-gray-100">
                {tag.name}
              </span>
              <input
                type="checkbox"
                checked={selectedTags.includes(tag.id)}
                onChange={(e) => {
                  if (e.target.checked)
                    setSelectedTags((prev) => [...prev, tag.id]);
                  else
                    setSelectedTags((prev) => prev.filter((t) => t !== tag.id));
                }}
                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-green-600 dark:border-gray-600"
              />
            </label>
          ))}
        </div>
        <button
          type="button"
          disabled={selectedTags < 1}
          onClick={() =>
            handleChange({
              tags: { set: selectedTags.map((id) => ({ id })) },
            })
          }
          className="mt-3 flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          <span>Confirmar</span>
        </button>
      </Accordion>

      {/* FUNIL */}
      <Accordion
        id="funnel"
        title="Funil"
        open={openSection === "funnel"}
        onToggle={toggleSection}
      >
        <div className="space-y-4">
          {funnels.map((funnel) => (
            <div key={funnel.id} className="space-y-2">
              {/* Funnel Title */}
              <div className="flex items-center space-x-2 pb-2">
                <div className="h-1 w-1 rounded-full bg-green-500"></div>
                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  {funnel.name}
                </h4>
              </div>

              {/* Funnel Steps */}
              {funnel.steps.map((step, index) => {
                const isSelected = selectedFunnel === step;
                return (
                  <label
                    key={`${funnel.id}-${step}`}
                    className={`flex cursor-pointer items-center justify-between rounded-lg border-2 p-3 text-sm font-medium transition-all duration-200 ${
                      isSelected
                        ? "border-green-400 bg-green-50 text-green-700 dark:border-green-500 dark:bg-green-900/20 dark:text-green-300"
                        : "dark:hover:bg-gray-750 border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </div>
                    <div
                      className={`h-5 w-5 rounded-full border-2 ${
                        isSelected
                          ? "border-green-500 bg-green-500 dark:border-green-400 dark:bg-green-400"
                          : "border-gray-300 dark:border-gray-600"
                      } flex items-center justify-center`}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <input
                      type="radio"
                      name="funnel-step"
                      checked={isSelected}
                      onChange={() => {
                        setSelectedFunnel(step);
                      }}
                      className="sr-only"
                    />
                  </label>
                );
              })}
            </div>
          ))}

          <button
            type="button"
            onClick={() => handleChange({ funnel: selectedFunnel })}
            disabled={!selectedFunnel}
            className="mt-2 flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:from-green-500 disabled:hover:to-green-600"
          >
            <Save className="h-4 w-4" />
            <span>Confirmar</span>
          </button>
        </div>
      </Accordion>

      {/* NOTAS */}
      <Accordion
        id="notes"
        title="Notas"
        open={openSection === "notes"}
        onToggle={toggleSection}
      >
        <textarea
          rows="4"
          value={conversationNote}
          onChange={(e) => setConversationNote(e.target.value)}
          placeholder="Adicionar nova nota..."
          className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-sm outline-none transition-colors focus:border-green-400 focus:ring-2 focus:ring-green-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:border-green-500 dark:focus:ring-green-900/30"
        />
        <button
          type="button"
          onClick={async () => {
            const newNote = {
              user: activeUser?.name,
              text: conversationNote,
              createdAt: new Date().toISOString(),
            };

            const updatedList = [...(notes || []), newNote];

            await handleChange({ notes: updatedList });

            setNotes(updatedList);
          }}
          disabled={!conversationNote.trim()}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          <span>Salvar Nota</span>
        </button>
        <div className="flex max-h-44 flex-col gap-3 overflow-y-auto">
          {notes && notes.length > 0 ? (
            notes
              .slice()
              .reverse()
              .map((note, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-gray-300 bg-gray-100 p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                >
                  <div className="flex items-center justify-between border-b border-gray-300 pb-2 dark:border-gray-800">
                    <span className="text-xs font-semibold text-green-700 dark:text-green-500">
                      {note.user || "Usuário"}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-500">
                      {new Date(note.createdAt).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="whitespace-pre-line pt-2 text-sm text-gray-900 dark:text-gray-200">
                    {note.text}
                  </p>
                </div>
              ))
          ) : (
            <p className="text-sm italic text-gray-500 dark:text-gray-400">
              Nenhuma nota adicionada ainda.
            </p>
          )}
        </div>
      </Accordion>

      {/* TRANSFERÊNCIA */}
      <Accordion
        id="transfer"
        title="Transferir"
        open={openSection === "transfer"}
        onToggle={toggleSection}
      >
        <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
          {users.map((user) => (
            <label
              key={user.id}
              className="dark:hover:bg-gray-750 flex cursor-pointer items-center justify-between rounded-lg border-2 border-gray-200 bg-white px-3 py-2.5 text-sm transition-all duration-200 hover:border-green-300 hover:bg-green-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-green-600"
            >
              <div className="flex items-center space-x-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white shadow-sm">
                  <Users className="h-4 w-4" />
                </div>
                <span className="font-medium text-gray-800 dark:text-gray-100">
                  {user.name}
                </span>
              </div>
              <input
                type="checkbox"
                checked={selectedUsers.includes(user.id)}
                disabled={user.id === activeUser?.id}
                onChange={(e) => {
                  if (e.target.checked)
                    setSelectedUsers((prev) => [...prev, user.id]);
                  else
                    setSelectedUsers((prev) =>
                      prev.filter((id) => id !== user.id),
                    );
                }}
                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-green-600 dark:border-gray-600"
              />
            </label>
          ))}
        </div>
        <button
          type="button"
          disabled={selectedUsers.length < 1}
          onClick={() => handleChange({ users: selectedUsers })}
          className="mt-3 flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Users className="h-4 w-4" />
          <span>Confirmar Transferência</span>
        </button>
      </Accordion>

      {analysis && (
        <div className={`mt-6 rounded-lg border p-4 ${
          ["alto", "critico"].includes(analysis.riskLevel)
            ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
            : "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
        }`}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
              <Brain className="h-4 w-4" /> Análise da IA
            </div>
            <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold uppercase text-gray-700 shadow-sm dark:bg-gray-800 dark:text-gray-200">
              risco {analysis.riskLevel}
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">{analysis.summary}</p>
          <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div><dt className="text-gray-500">Rumo</dt><dd className="font-medium text-gray-800 dark:text-gray-200">{analysis.conversationDirection}</dd></div>
            <div><dt className="text-gray-500">Satisfação</dt><dd className="font-medium text-gray-800 dark:text-gray-200">{analysis.customerSatisfaction}</dd></div>
            <div><dt className="text-gray-500">Cliente</dt><dd className="font-medium text-gray-800 dark:text-gray-200">{analysis.customerScore ?? "—"}/10</dd></div>
            <div><dt className="text-gray-500">Atendimento</dt><dd className="font-medium text-gray-800 dark:text-gray-200">{analysis.agentScore ?? "—"}/10</dd></div>
          </dl>
          <div className="mt-3 space-y-2 rounded-md bg-white/70 p-3 text-xs dark:bg-gray-900/40">
            <div>
              <p className="font-semibold text-gray-700 dark:text-gray-200">Comportamento do cliente</p>
              <p className="text-gray-600 dark:text-gray-300">
                Tom: {analysis.customerBehavior?.tone || "não identificado"}. Evolução: {analysis.customerBehavior?.evolution || "não identificada"}.
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-700 dark:text-gray-200">Conduta do atendimento</p>
              <p className="text-gray-600 dark:text-gray-300">
                Tom: {analysis.agentBehavior?.tone || "não identificado"}. Empatia: {analysis.agentBehavior?.empathy || "não identificada"}. Conduta: {analysis.agentBehavior?.conduct || "não identificada"}.
              </p>
              {analysis.agentBehavior?.agents?.map((agent, index) => (
                <p key={index} className="mt-1 text-gray-600 dark:text-gray-300">
                  {agent.name}: {agent.score ?? "—"}/10 — {agent.conduct}
                </p>
              ))}
            </div>
          </div>
          {analysis.dissatisfactionSignals?.length > 0 && (
            <div className="mt-3">
              <p className="flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-300">
                <AlertTriangle className="h-3.5 w-3.5" /> Sinais de insatisfação
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-gray-700 dark:text-gray-300">
                {analysis.dissatisfactionSignals.map((signal, index) => <li key={index}>{signal}</li>)}
              </ul>
            </div>
          )}
          {analysis.recommendedActions?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">Próximas ações</p>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-gray-700 dark:text-gray-300">
                {analysis.recommendedActions.map((action, index) => <li key={index}>{action}</li>)}
              </ul>
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <p className="text-[11px] text-gray-500">
              {analysis.source === "AUTOMATIC" ? "Análise automática" : "Análise manual"} · {new Date(analysis.createdAt).toLocaleString("pt-BR")}
            </p>
            <AnalysisCoverage analysis={analysis} />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleAnalyze}
        disabled={analyzing}
        className="mt-4 w-full rounded-lg bg-yellow-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-yellow-700 hover:shadow-lg active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {analyzing ? "Analisando..." : analysis ? "Atualizar Análise" : "Analisar Conversa"}
      </button>

      <button
        type="button"
        onClick={handleCheckGoogle}
        disabled={checkingGoogle}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        title="Procura este telefone na agenda do Google e salva se não existir"
      >
        {googleState?.status === "SYNCED" ? (
          <BookUser className="h-4 w-4 text-green-600 dark:text-green-400" />
        ) : (
          <BookUser className="h-4 w-4" />
        )}
        {checkingGoogle
          ? "Consultando agenda..."
          : googleState?.status === "SYNCED"
            ? "Na agenda do Google — verificar de novo"
            : "Verificar na agenda do Google"}
      </button>

      {googleState?.error && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          {googleState.error}
        </p>
      )}

      <button
        type="button"
        onClick={handleToggleAutoAnalysis}
        disabled={togglingAuto}
        className={`mt-2 flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
          autoAnalysisEnabled
            ? "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            : "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
        }`}
        title="A análise manual continua disponível mesmo com a automática pausada"
      >
        {autoAnalysisEnabled ? (
          <BellOff className="h-4 w-4" />
        ) : (
          <Bell className="h-4 w-4" />
        )}
        {autoAnalysisEnabled
          ? "Pausar Análise Automática"
          : "Retomar Análise Automática"}
      </button>

      <button
        type="button"
        onClick={() => {
          deleteConversation(selectedChat.id);
          setSelectedChat(null);
        }}
        className="mt-4 w-full rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-red-700 hover:shadow-lg active:scale-95"
      >
        Deletar Conversa
      </button>
    </div>
  );
};

export default ClientSidebar;
